import React, { useEffect } from 'react';
import {
	Autocomplete,
	FormControl,
	FormControlProps,
	FormHelperText,
	TextField,
	TextFieldProps,
} from '@mui/material';
import {
	Service as OrganizationService,
	useSelectServices_GetServicesWhereQuery,
} from '#types';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import { gql } from '@apollo/client';
import { useToast } from '../../hooks/useToast';
import { Spinner } from '../../blocks/Spinner';
import useErrorScrollContext from '../../hooks/useErrorScrollContext';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getServicesWhere = gql`
	query SelectServices_GetServicesWhere {
		getServicesWhere {
			id
			name
		}
	}
`;

type Service = Pick<OrganizationService, 'id' | 'name'>;

export type SelectServicesProps = {
	value?: Service[] | null;
	onChange: ( services: Service[] ) => void;
	errorText?: string;
	dataCy?: string;
	chipLimitDesktop?: number;
	chipLimitMobile?: number;
} & Omit<FormControlProps, 'error' | 'onChange' | 'onBlur'> &
	Pick<TextFieldProps, 'onBlur' | 'disabled'>;

const SelectServices: React.FC<SelectServicesProps> = ( {
	value,
	onChange,
	errorText,
	onBlur,
	dataCy,
	chipLimitDesktop,
	chipLimitMobile,
	disabled,
	...formControlProps
} ) => {
	const { showError } = useToast();
	const { isDesktop } = useDeviceDetection();
	const { data, error, loading } = useSelectServices_GetServicesWhereQuery();

	const ref = useErrorScrollContext( !!errorText );

	useEffect( () => {
		if ( error ) {
			showError( error );
		}
	}, [ error, showError ] );

	if ( loading ) {
		return <Spinner />;
	}

	return (
		<FormControl { ...formControlProps } error={ !!errorText }>
			<Autocomplete<Service, true, false>
				ref={ ref }
				multiple
				filterSelectedOptions
				limitTags={ isDesktop ? chipLimitDesktop : chipLimitMobile }
				value={ value || [] }
				onChange={ ( _, value ) => onChange( value ) }
				options={ data?.getServicesWhere || [] }
				getOptionLabel={ ( option ) => option.name }
				isOptionEqualToValue={ ( option, value ) => option.id === value.id }
				autoHighlight
				disabled={ disabled }
				forcePopupIcon={ false } // to hide the option to display the list without typing anything
				renderInput={ ( params ) => (
					<TextField
						{ ...params }
						label='What services do you provide?'
						onBlur={ onBlur }
						error={ !!errorText }
						variant='standard'
						slotProps={ {
							...params?.slotProps,
							htmlInput: {
								...params?.slotProps?.htmlInput,
								'data-cy': dataCy,
							},
						} }
					/>
				) }
			/>
			{ errorText ? <FormHelperText>{ errorText }</FormHelperText> : null }
		</FormControl>
	);
};

export default SelectServices;
